import type { Debt } from '@familywallet/shared';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { formatDay } from '../lib/dates';
import { formatAmount } from '../lib/money';
import { IconSquare, listRowClass } from './ui/card';

/** A credit item with a due date: who, what and when, what is still owed, state. */
export function CreditDueRow({ debt, today }: { debt: Debt; today: string }) {
  const { t, i18n } = useTranslation();
  const credit = debt.credit!;
  const date = formatDay(credit.dueOn!, today, i18n.language, {
    today: t('activity.today'),
    yesterday: t('activity.yesterday'),
  });
  const late = credit.dueState === 'overdue';
  return (
    <Link to={`/debts/${debt.id}`} className={listRowClass}>
      <IconSquare>{debt.person.name.slice(0, 1).toUpperCase()}</IconSquare>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[15px] font-medium">{debt.person.name}</span>
        <span className="truncate text-xs text-muted">
          {[credit.name || t('credit.item'), t('credit.due', { date })].join(' · ')}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end">
        <span dir="ltr" className="text-[15px] font-semibold tabular-nums">
          {formatAmount(debt.outstanding, debt.currency, i18n.language)}
        </span>
        <span className={late ? 'text-xs font-semibold text-expense' : 'text-xs text-muted'}>
          {t(`debts.states.${credit.dueState!}`)}
        </span>
      </span>
    </Link>
  );
}

/** A debt's next installment in a list: who, which installment and when, amount, state. */
export function InstallmentRow({ debt, today }: { debt: Debt; today: string }) {
  const { t, i18n } = useTranslation();
  const next = debt.nextInstallment!;
  const date = formatDay(next.dueOn, today, i18n.language, {
    today: t('activity.today'),
    yesterday: t('activity.yesterday'),
  });
  const late = next.state === 'overdue';
  return (
    <Link to={`/debts/${debt.id}`} className={listRowClass}>
      <IconSquare>{debt.person.name.slice(0, 1).toUpperCase()}</IconSquare>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[15px] font-medium">{debt.person.name}</span>
        <span className="truncate text-xs text-muted">
          {t('debts.installmentOf', { number: next.number, date })}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end">
        <span dir="ltr" className="text-[15px] font-semibold tabular-nums">
          {formatAmount(next.amount, debt.currency, i18n.language)}
        </span>
        <span className={late ? 'text-xs font-semibold text-expense' : 'text-xs text-muted'}>
          {late && debt.overdueInstallments > 1
            ? t('debts.overdueBadge', { count: debt.overdueInstallments })
            : t(`debts.states.${next.state}`)}
        </span>
      </span>
    </Link>
  );
}
